import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { 
  User, 
  Mail, 
  Phone, 
  Building, 
  Shield,
  ArrowLeft,
  Save,
  UserPlus
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import DepartmentCheckboxes from '../../components/DepartmentCheckboxes';
import toast from 'react-hot-toast';
import api from '../../services/api';
import axios from 'axios';

interface UserForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: 'admin' | 'technician' | 'client' | 'tech';
  company: string;
  address: {
    street: string;
    city: string;
    postalCode: string;
    country: string;
  };
  departements: string[]; 
  permissions: string[];
  isActive: boolean;
  password: string;
  confirmPassword: string;
}

const AddUser: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  
  const { register, handleSubmit, watch, formState: { errors }, setValue, reset } = useForm<UserForm>({
    defaultValues: {
      role: 'client',
      isActive: true,
      address: { country: 'France' },
      permissions: [],
      departements: []
    }
  });

  const selectedRole = watch('role');

  const rolePermissions = {
    admin: [
      'users.manage',
      'companies.manage',
      'requests.manage',
      'quotes.manage',
      'payments.manage',
      'reports.manage',
      'messages.manage'
    ],
    technician: [
      'requests.read',
      'requests.update',
      'quotes.read',
      'installations.manage',
      'reports.create',
      'messages.manage'
    ],
    client: [
      'requests.create',
      'requests.read',
      'quotes.read',
      'quotes.respond',
      'installations.read',
      'payments.read',
      'messages.create'
    ],
    tech: [
      'requests.read',
      'requests.update',
      'quotes.read',
      'installations.manage',
      'reports.create',
      'messages.manage'
    ]
  };

  const onSubmit = async (data: UserForm) => {
    setIsLoading(true);
    try {
      const userData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        phone: data.phone,
        role: data.role === 'tech' ? 'technician' : data.role,
        company: data.company,
        address: data.address,
        departements: data.departements,
        isActive: data.isActive
      };

      await api.post('/users', userData);

      toast.success('Utilisateur créé avec succès — la page reste ouverte 👍');
      // ➜ On ne navigue pas : on reste ici et on vide le formulaire
      reset({
        role: 'client',
        isActive: true,
        address: { country: 'France', street: '', city: '', postalCode: '' },
        permissions: [],
        departements: [],
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        company: ''
      });
    } catch (error: unknown) {
      console.error('Erreur détaillée:', error);
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message || 'Erreur lors de la création');
      } else {
        toast.error('Une erreur inconnue est survenue lors de la création.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/admin/users')}>
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <UserPlus className="w-8 h-8 mr-3 text-primary-600" />
                Nouvel Utilisateur
              </h1>
              <p className="text-gray-600 mt-2">
                Créer un nouveau compte utilisateur dans le système
              </p>
            </div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-8"
          // Anti-submit “accidentel” par Enter si tu veux le bloquer :
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') e.preventDefault(); }}
          autoComplete="off"
        >
          {/* Informations personnelles */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <User className="w-5 h-5 mr-2 text-primary-600" />
              Informations Personnelles
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prénom *
                </label>
                <input
                  type="text"
                  {...register('firstName', { required: 'Le prénom est requis' })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Jean"
                  autoComplete="given-name"
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom *
                </label>
                <input
                  type="text"
                  {...register('lastName', { required: 'Le nom est requis' })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Dupont"
                  autoComplete="family-name"
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email *
                </label>
                <input
                  type="email"
                  {...register('email', { 
                    required: 'L\'email est requis',
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: 'Email invalide'
                    }
                  })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="jean.dupont@email.com"
                  autoComplete="email"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Téléphone
                </label>
                <input
                  type="tel"
                  {...register('phone')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="06 12 34 56 78"
                  autoComplete="tel-national"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Building className="w-4 h-4 inline mr-1" />
                  Entreprise
                </label>
                <input
                  type="text"
                  {...register('company')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Nom de l'entreprise"
                  autoComplete="organization"
                />
              </div>
            </div>
          </Card>

          {/* Rôle et permissions */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <Shield className="w-5 h-5 mr-2 text-primary-600" />
              Rôle et Permissions
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rôle *
                </label>
                <select
                  {...register('role', { required: 'Le rôle est requis' })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="client">Client</option>
                  <option value="technician">Technicien</option>
                  <option value="admin">Administrateur</option>
                </select>
                {errors.role && (
                  <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
                )}
              </div>

              {['technician', 'client'].includes(selectedRole) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Départements *
                  </label>
                  <DepartmentCheckboxes
                    value={watch('departements') || []}
                    onChange={(vals) => setValue('departements', vals, { shouldValidate: true })}
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Statut
                </label>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    {...register('isActive')}
                    className="mr-2 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Compte actif</span>
                </div>
              </div>
            </div>

            {/* Aperçu des permissions */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Permissions accordées
              </label>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {rolePermissions[selectedRole as keyof typeof rolePermissions].map((permission) => (
                    <div key={permission} className="flex items-center text-sm text-gray-600">
                      <div className="w-2 h-2 bg-primary-500 rounded-full mr-2"></div>
                      {permission.replace('.', ' - ')}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          {/* Adresse */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Adresse</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rue
                </label>
                <input
                  type="text"
                  {...register('address.street')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="123 Rue de la République"
                  autoComplete="address-line1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ville
                </label>
                <input
                  type="text"
                  {...register('address.city')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Paris"
                  autoComplete="address-level2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Code Postal
                </label>
                <input
                  type="text"
                  {...register('address.postalCode')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="75001"
                  autoComplete="postal-code"
                />
              </div>
            </div>
          </Card>

          {/* Mot de passe */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Mot de Passe</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mot de passe *
                </label>
                <input
                  type="password"
                  {...register('password', { 
                    required: 'Le mot de passe est requis',
                    minLength: {
                      value: 6,
                      message: 'Le mot de passe doit contenir au moins 6 caractères'
                    }
                  })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmer le mot de passe *
                </label>
                <input
                  type="password"
                  {...register('confirmPassword', { required: 'Veuillez confirmer le mot de passe' })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
                )}
              </div>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/admin/users')}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              isLoading={isLoading}
              size="lg"
            >
              <Save className="w-4 h-4 mr-2" />
              Créer l'Utilisateur
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default AddUser;
